import { Box, makeStyles, Typography } from '@material-ui/core';
import { t, Trans } from '@lingui/macro';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { NavigateBefore } from '@material-ui/icons';
import { merge } from 'lodash';
import { i18n } from '@lingui/core';

import AuthPanel from './AuthPanel';
import UserDetailSection from '../pages-admin/users/UserDetailSection';
import { useFieldWatch, useValidationErrors, USE_FORM_DEFAULT_OPTIONS } from '../../utilities/FormUtilities';
import PasswordRequirements from '../pages-admin/users/PasswordRequirements';
import NgpPasswordField from '../form/NgpPasswordField';
import {
    CONFIRM_PASSWORD_FIELD,
    PASSWORD_FIELD,
    USER_NAME_FIELD,
    USER_STATUS_INVITED_OPTION,
} from '../pages-admin/users/UserConstants';
import { useDataUrl, useGlobalDataService } from '../../services/ApiDataService';
import { RowFlexLayout } from '../layout';
import AuthButton from './AuthButton';
import AuthProgressButton from './AuthProgressButton';
import { useRequestState } from '../../utilities/Hooks';
import Link from '../links/Link';
import { isBadRequestError, rethrowError } from '../../utilities/RequestUtilities';
import { Navigate } from '../../utilities/PgmUtilities';
import { LOGIN_URL } from './LoginPage';

const useStyles = makeStyles((theme) => ({
    root: {
        '& .MuiFormControl-root': {
            marginTop: theme.spacing(2),
        },
    },
}));

export default function SignUpPage() {
    const formMethods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { activateToken } = useParams();
    const [user, isLoadingUser, userError] = useSignupUser(activateToken);
    const classes = useStyles();

    const { signUp, isLoading, error: signupError, signedUp } = useSignUpHandler(user, activateToken);
    useValidationErrors(signupError, formMethods.setError);

    const { reset } = formMethods;
    useEffect(() => {
        reset(user);
    }, [reset, user]);

    if (signedUp === true) {
        return <Navigate to="/" />;
    }

    if (!activateToken || userError) {
        return (
            <AuthPanel>
                <Typography variant="subtitle2" color="error">
                    <Trans>Error: The provided sign-up token is invalid or has already been used</Trans>
                </Typography>
                <RowFlexLayout hRight>
                    <AuthButton startIcon={<NavigateBefore />} container={<Box mt={4} />} component={Link} to="/login">
                        <Trans>Go to Login</Trans>
                    </AuthButton>
                </RowFlexLayout>
            </AuthPanel>
        );
    }

    return (
        <AuthPanel loading={isLoadingUser} className={classes.root}>
            <FormProvider {...formMethods}>
                <form onSubmit={formMethods.handleSubmit(signUp)}>
                    <Typography variant="h6">
                        <Trans>Sign Up for nGeniusPULSE</Trans>
                    </Typography>

                    {typeof signupError === 'string' && (
                        <Typography variant="subtitle2" color="error">
                            {signupError}
                        </Typography>
                    )}

                    <UserDetailSection showSourceField={false} showStatusAndRole={false} />

                    <PasswordSection />

                    <RowFlexLayout hRight>
                        <AuthProgressButton type="submit" loading={isLoading} container={<Box mt={4} />}>
                            <Trans>Sign Up</Trans>
                        </AuthProgressButton>
                    </RowFlexLayout>
                </form>
            </FormProvider>
        </AuthPanel>
    );
}

function PasswordSection() {
    const password = useFieldWatch(PASSWORD_FIELD);

    return (
        <>
            <PasswordRequirements />

            <NgpPasswordField
                fieldName={PASSWORD_FIELD}
                label={<Trans>Password</Trans>}
                required
                inputProps={{ size: 65, maxLength: 65 }}
            />

            <NgpPasswordField
                fieldName={CONFIRM_PASSWORD_FIELD}
                label={<Trans>Confirm Password</Trans>}
                required
                inputProps={{ size: 65, maxLength: 65 }}
                fieldRules={{
                    validate: {
                        passwordsEqual: (value) => (value !== password ? i18n._(t`Passwords do not match`) : true),
                    },
                }}
            />
        </>
    );
}

const useSignupUser = (activateToken) => useDataUrl(`/ipm/public/signup/${activateToken}`);

function useSignUpHandler(user, activateToken) {
    const dataService = useGlobalDataService();
    const { data, isLoading, error, methods } = useRequestState();

    const signUp = useCallback(
        async (formData) => {
            try {
                methods.onInit();

                const mergedUserData = merge({}, user, formData);

                const query = JSON.stringify({
                    activateToken,
                    userStatus: USER_STATUS_INVITED_OPTION,
                });

                await dataService.put(`/ipm/public/signup/${activateToken}`, mergedUserData, { query }, rethrowError);

                const loginData = {
                    emailOrUsername: mergedUserData?.[USER_NAME_FIELD],
                    password: mergedUserData?.[PASSWORD_FIELD],
                };

                await dataService.post(LOGIN_URL, loginData, null, rethrowError);

                methods.onLoad(true);
            } catch (e) {
                const validations = e?.responseBody?.validations;
                if (isBadRequestError(e) && validations) {
                    methods.onError(validations);
                } else {
                    methods.onError(String(e));
                }
            }
        },
        [activateToken, dataService, methods, user]
    );

    return { signedUp: data === true, isLoading, error, signUp };
}
